import type { PartCustodyHolding } from "./part-custody.js";

/**
 * Everything the Parts screen's summary panel shows for one part.
 *
 * `GET /parts/:id/summary`. One part, every shelf it sits on, where it has
 * been bought, and its photos, so the panel needs a single request.
 */

/** Stock on one shelf. Quantities are 3-place decimal strings, never money. */
export interface PartSummaryStock {
  locationId: string;
  locationName: string;
  onHand: string;
  /** On hand less what is reserved: what can actually be issued. */
  available: string;
  lastCountedAt: string | null;
  lastCountVariance: string | null;
}

/**
 * One time this part was bought.
 *
 * `purchase_order` is a line on one of our purchase orders. `invoice` is a
 * vendor's invoice line posted to a work order with this part on it.
 */
export interface PartPurchase {
  source: "purchase_order" | "invoice";
  /** "PO-12" or "WO-4031". */
  reference: string;
  /** The purchase order or work order the line belongs to. */
  referenceId: string;
  vendorId: string | null;
  vendorName: string | null;
  /** ISO timestamp: when the order was raised or the invoice line posted. */
  at: string;
  quantity: string | null;
  unitCost: string | null;
  currency: string;
  /** The purchase order's status, e.g. "RECEIVED"; null for an invoice line. */
  status: string | null;
}

export interface PartPhoto {
  documentId: string;
  isPrimary: boolean;
  fileName: string | null;
}

export interface PartSummary {
  partId: string;
  partNumber: string;
  description: string;
  manufacturer: string | null;
  unitOfMeasure: string;
  unitCost: string | null;
  currency: string;
  tags: string[];
  usualSupplier: { vendorId: string; name: string } | null;
  stock: PartSummaryStock[];
  totalOnHand: string;
  /** Total on hand × unit cost, 2 places; null when the part has no cost. */
  totalValue: string | null;
  /** Newest first, at most 10. */
  purchases: PartPurchase[];
  /** Primary first, then newest. */
  photos: PartPhoto[];
  /** Part coverage recorded in inventory, separate from a vendor service warranty. */
  underWarranty?: boolean;
  warrantyNote?: string | null;
  /** User-entered product page; never inferred from the supplier or purchases. */
  productUrl?: string | null;
  /**
   * TRBUD-416. What each mechanic holds of this part from hand-overs that named
   * no job. Shelves only are in `stock` and `totalOnHand`; parts in a mechanic's
   * hands are listed here, apart, because no shelf action applies to them.
   */
  custody?: PartCustodyHolding[];
  /** The sum of `custody`, 3 places. */
  custodyTotal?: string;
}

/** The image types a part photo or a profile photo may be. */
export const PHOTO_MIME_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;

/** Largest profile photo the server keeps (the browser shrinks it first). */
export const USER_AVATAR_MAX_BYTES = 262_144;

/**
 * The square, in pixels, a list thumbnail is drawn at (TRBUD-354). Twice the
 * largest size a list shows it, so it stays sharp on a high-density screen.
 */
export const LIST_THUMBNAIL_SIZE = 96;

/** Largest list thumbnail the server keeps (`document_thumbnail`). */
export const DOCUMENT_THUMBNAIL_MAX_BYTES = 65_536;

/** Largest vendor logo the server keeps (`vendor_logo`; the browser shrinks it first). */
export const VENDOR_LOGO_MAX_BYTES = 262_144;

/**
 * Initials for a person's name: "Matt Farless" is "MF", "Cher" is "CH",
 * an email address uses the part before the @.
 */
export function initialsFor(name: string): string {
  const base = name.includes("@") ? name.split("@")[0]! : name;
  const words = base
    .replace(/[._-]+/g, " ")
    .split(/\s+/)
    .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ""))
    .filter((w) => w.length > 0);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase();
}
